import { equals } from 'ramda';
import type { ExcutingJobInfo, JobInfo } from './types';
import { INITIAL_OPTION } from './types';
import { DefaultJobTimeoutMillisecond } from './constants';
import { getPrefixedLogger, prefixes } from '../rwc-logger';
import { eraseLocallyStored, readLocallyStored, writeLocallyStored } from '../local-storage';

const UTIL_NAME = 'job-scheduler';
const logger = getPrefixedLogger(prefixes.VC_UTILS, UTIL_NAME);

/**
 * management of scheduled jobs,such as polling task
 * multiple scheduled jobs can be added, use key to distinguish
 * @class
 */
class JobScheduler {
    constructor(namespace = 'rcv') {
        this.namespace = namespace;
    }
    private namespace = '';
    /**
     * map to store job infos
     */
    private readonly _jobMap = new Map<string, JobInfo>();
    private readonly _excutingJobMap = new Map<string, ExcutingJobInfo>();

    private _getJobInfo(key: string): JobInfo {
        return this._jobMap.get(key) as JobInfo;
    }

    /**
     * start schedule job,there are three kinds of schedule job
     * IGNORE_FIRST_TIME、FIRE_IMMEDIATELY、BASE_ON_LAST_TIME
     * @param key
     * @param initialOption
     */
    private _scheduleJob = (key: string, initialOption: INITIAL_OPTION): void => {
        switch (initialOption) {
            case INITIAL_OPTION.IGNORE_FIRST_TIME:
                this._scheduleAndIgnoreFirstTime(key);
                break;
            case INITIAL_OPTION.FIRE_IMMEDIATELY:
                this._fireImmediately(key);
                break;
            case INITIAL_OPTION.BASE_ON_LAST_TIME:
                this._scheduleBasedOnLastTime(key);
                break;
            default:
                this._scheduleBasedOnLastTime(key);
        }
    };
    /**
     * ignore first time execute, then set timer for next execute
     * @param key
     */
    private _scheduleAndIgnoreFirstTime(key: string): void {
        const info = this._getJobInfo(key);
        this._setLastSuccessTime(key, Date.now());
        this._setTimer(key, info.intervalSeconds * 1000);
    }
    /**
     * execute immediately and set timer
     * @param key
     */
    private _fireImmediately(key: string): void {
        this._executeJob(key);
    }
    /**
     * execute based on the last time
     * @param key
     */
    private _scheduleBasedOnLastTime(key: string): void {
        const info = this._getJobInfo(key);

        const lastSuccessTime: number = this._getLastSuccessTime(key);
        if (this._canExecute(info, lastSuccessTime)) {
            this._executeJob(key);
            return;
        }

        const interval = info.intervalSeconds * 1000 - (Date.now() - lastSuccessTime);
        this._setTimer(key, interval > 0 ? interval : info.intervalSeconds * 1000);
    }
    /**
     * cancel job task
     * @param key
     */
    private _cancelJobTask(key: string) {
        const info = this._excutingJobMap.get(key);
        if (info?.jobId) {
            clearTimeout(info.jobId);
        }
        this._jobMap.delete(key);
        this._excutingJobMap.delete(key);
    }
    private _getCacheKeyWithNamespace(key: string): string {
        return `job-schedule.${this.namespace}.${key}`;
    }
    /**
     * get last success time from localstorage
     * @param key
     */
    private _getLastSuccessTime(key: string): number {
        const cacheKey = this._getCacheKeyWithNamespace(key);
        const lastSuccessTime = readLocallyStored(cacheKey);
        return lastSuccessTime ? Number(lastSuccessTime) : 0;
    }
    /**
     * set last success time to localstorage
     * @param key
     * @param value
     */
    private _setLastSuccessTime(key: string, value: number) {
        const cacheKey = this._getCacheKeyWithNamespace(key);
        writeLocallyStored(cacheKey, value, true);
    }
    /**
     * remove last success time from localstorage
     * @param key
     */
    private _removeLastSuccessTime(key: string) {
        const cacheKey = this._getCacheKeyWithNamespace(key);
        eraseLocallyStored(cacheKey, true);
    }
    /**
     * set job timer and store job id to _jobMap
     * @param key
     * @param interval
     */
    private _setTimer(key: string, interval: number) {
        const info = this._excutingJobMap.get(key);
        if (info?.jobId) {
            clearTimeout(info.jobId);
        }
        const jobId = window.setTimeout(() => {
            this._executeJob(key);
        }, interval);
        this._excutingJobMap.set(key, { ...info, jobId });
    }
    /**
     * whether the job needs to be executed now according to the last successful time
     * @param info
     * @param lastSuccessTime
     */
    private _canExecute(info: JobInfo, lastSuccessTime: number): boolean {
        const currentTime: number = Date.now();
        const canExecute = currentTime - lastSuccessTime >= info.intervalSeconds * 1000;
        return canExecute;
    }
    /**
     * execute job, the status depends on finished or timeout
     * @param key
     */
    private _executeJob(key: string) {
        const info = this._getJobInfo(key);
        let timeoutId;
        const timeoutPromise = new Promise<void>(resolve => {
            timeoutId = window.setTimeout(
                () => {
                    this._timeoutJob(key);
                    resolve();
                },
                info.timeoutMillisecond ? info.timeoutMillisecond : DefaultJobTimeoutMillisecond
            );
        });
        const jobPromise = this._execute(key).then(() => timeoutId && clearTimeout(timeoutId));
        return Promise.race([timeoutPromise, jobPromise]);
    }
    /**
     * execute timeout, add log
     * @param key
     */
    private _timeoutJob(key: string) {
        logger.debug(`_timeoutJob, ${key}, trace job whether is timeout`);
    }
    /**
     * the main logic of execute and callback
     * @param key
     */
    private async _execute(key: string): Promise<void> {
        const excutingJobInfo = this._excutingJobMap.get(key);
        if (excutingJobInfo?.isExecuting) {
            return;
        }
        this._excutingJobMap.set(key, { ...excutingJobInfo, isExecuting: true });

        const info = this._getJobInfo(key);
        const onExecutionFinished = (successful: boolean) => {
            if (!info || !equals(info, this._getJobInfo(key))) {
                return;
            }
            this._excutingJobMap.set(key, { ...excutingJobInfo, isExecuting: false });

            if (successful) {
                this._setLastSuccessTime(info.key, Date.now());
            }
            this._setTimer(key, info.intervalSeconds * 1000);
        };
        try {
            await info.executeFunc();
            onExecutionFinished(true);
        } catch (err) {
            onExecutionFinished(false);
            logger.info('JobScheduler _execute failed:', err);
            throw err;
        }
    }
    /**
     * whether the current job is in the job map
     * @param key
     */
    public isJobInSchedule = (key: string) => this._jobMap.has(key);

    /**
     * schedule job
     * @param key unique key used to identify the current schedule job
     * @param executeFunc the main execute function for schedule
     * @param intervalSeconds will schedule intervalSeconds_timer after job finish
     * @param initialOption schedule type, BASE_ON_LAST_TIME/FIRE_IMMEDIATELY/IGNORE_FIRST_TIME
     * @param timeoutMillisecond timeout limit
     */
    public schedulePeriodicJob(
        key: string,
        executeFunc: () => Promise<void> | void,
        intervalSeconds: number,
        initialOption: INITIAL_OPTION,
        timeoutMillisecond: number = DefaultJobTimeoutMillisecond
    ) {
        if (this._jobMap.has(key)) {
            this.cancelJob(key, false);
        }
        const info: JobInfo = {
            key: key,
            executeFunc,
            intervalSeconds,
            timeoutMillisecond,
        };
        this._jobMap.set(key, info);
        this._scheduleJob(key, initialOption);
    }
    /**
     * cancel job and remove job from jobMap
     * @param key
     * @param removeLastTime
     */
    public cancelJob(key: string, removeLastTime = false): void {
        if (!this.isJobInSchedule(key)) {
            return;
        }
        this._cancelJobTask(key);

        if (removeLastTime) {
            this._removeLastSuccessTime(key);
        }
    }
    /**
     * clear all scheduled jobs
     */
    public clear() {
        this._jobMap.forEach((info: JobInfo, key: string) => {
            this._cancelJobTask(key);
            this._removeLastSuccessTime(key);
        });
        this._jobMap.clear();
    }
}

export default JobScheduler;
